import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup, useSlotGroup, useGallery, useTokens } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance } from '../utils/luminance';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { CTA_SOLID, CTA_SURFACE_HOVER, CtaStyles, ctaOverLight } from './_cta';

// ============================================================================
// Neighborhood Intro — arched lifestyle photo beside a headline + blurb + CTA.
// Mirrors the demo's "Apartment in {area}" section directly below the hero.
// Styled to match PropertyOverview: same arched frame, title treatment, and
// canonical CTA so the two home-page sections read as one system.
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Lifestyle Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Neighborhood lifestyle' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function NeighborhoodIntro() {
  useRevealStyles();
  const containerRef = useScrollReveal<HTMLDivElement>();

  const gallery = useGallery();
  const img = useSlotGroup('neighborhoodintro.image', imageDef);
  const cta = useSlotGroup('neighborhoodintro.cta', ctaDef);

  // Match PropertyOverview's title color logic: when the theme's on-primary
  // text color is dark, the property has a light primary — a light-primary
  // title reads too faint, so fall back to solid black. Otherwise keep the
  // branded primary color.
  const tokens = useTokens();
  const titleColor =
    relativeLuminance(tokens?.color?.textOnPrimary) < 0.5
      ? '#000000'
      : 'var(--vibe-color-primary)';

  const galleryFallback = gallery[2]?.image || gallery[0]?.image || '';
  const displayImage = img.image || galleryFallback;

  return (
    <section className="pt-12 md:pt-20 pb-0 bg-[var(--vibe-color-background)] overflow-hidden">
      <CtaStyles />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={containerRef}
          className="reveal-fade-up grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center"
        >
          {/* Arched image */}
          <div className="overview-arch-frame mx-auto w-full max-w-md">
            <EditableSlotGroup groupPath="neighborhoodintro.image" definition={imageDef}>
              <div className="overview-arch-clip aspect-[3/4]">
                {displayImage ? (
                  <VibesitesImage
                    url={displayImage}
                    alt={img.alt || 'Neighborhood lifestyle'}
                    sizes="(min-width: 768px) 28rem, 100vw"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_20%,var(--vibe-color-background))]" />
                )}
              </div>
            </EditableSlotGroup>
          </div>

          {/* Copy */}
          <div className="text-center md:text-left">
            <RichTextContent
              path="neighborhoodintro.heading"
              defaultValue='<p>Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
              as="h2"
              className="font-heading text-[clamp(1.75rem,8vw,2.5rem)] sm:text-5xl md:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] break-words [text-wrap:balance]"
              style={{ color: titleColor }}
            />

            <RichTextContent
              path="neighborhoodintro.body"
              defaultField="property_short_description"
              defaultValue="<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>"
              as="div"
              className="font-body text-base sm:text-lg text-[var(--vibe-color-text)]/75 max-w-xl leading-relaxed [text-wrap:pretty] [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup groupPath="neighborhoodintro.cta" definition={ctaDef}>
              <a
                href={cta.url || '/floor-plans'}
                target={cta._target || '_self'}
                className={`mt-10 ${CTA_SOLID} ${CTA_SURFACE_HOVER}`}
                style={ctaOverLight}
              >
                {cta.label || 'View Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .overview-arch-frame {
          border: 2px solid var(--vibe-color-primary);
          border-radius: 50% 50% 6px 6px / 42% 42% 6px 6px;
          padding: 14px;
        }
        .overview-arch-clip {
          border-radius: 50% 50% 3px 3px / 40% 40% 3px 3px;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodIntro',
  displayName: 'Neighborhood Intro',
  tokens: {
    'neighborhoodintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue:
        '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'neighborhoodintro.image': imageDef,
    'neighborhoodintro.cta': ctaDef,
  },
};

(NeighborhoodIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodIntro;
